<template>
  <div class="view" v-if="canEdit" v-cloak>
    <EditCreateTitleBox :label="title">
      <!-- optional action buttons here -->
    </EditCreateTitleBox>
    <div v-if="isLoading" class="loader-container">
      <Loader :loading="isLoading" size="sm"></Loader>
    </div>
    <div v-else class="weighing-on-scale-container">
      <!-- Left Box -->
      <div class="left-box">
        <!-- Step Flow -->
        <StepsBar :steps="steps" :current-step="currentStep" @step-clicked="handleStepClick" />

        <!-- Step Content -->
        <div class="weighing-on-scale-step-content">
          <DxTabPanel
            :data-source="steps"
            :selected-index="currentStep - 1"
            :loop="false"
            :animation-enabled="false"
            :swipe-enabled="false"
            :show-nav-buttons="false"
            @selection-changed="onStepChanged"
            class="weighing-on-scale-dx-tabpanel"
          >
            <template #item="{ data: step }">
              <component
                :number="step.number"
                :is="step.component"
                :form-data="formData"
                :field-report-usa-automation-enabled="fieldReportUsaAutomationEnabled"
                :ref="step.component"
                @update-form-data="updateFormData"
              />
            </template>
          </DxTabPanel>
        </div>
      </div>

      <!-- Right Box -->
      <RightInfoPanel :form-data="formData" :saving="isSaving" @save="save" />

      <WarehouseSplitPopup ref="warehouseSplitPopup" />
      <LotNumberResolvePopup
        v-model="lotResolvePopup.visible"
        :title="lotResolvePopup.title"
        :message="lotResolvePopup.message"
        :mode="lotResolvePopup.mode"
        :lot-options="lotResolvePopup.options"
        :input-placeholder="lotResolvePopup.inputPlaceholder"
        :confirm-text="lotResolvePopup.confirmText"
        :continue-without-lot-text="lotResolvePopup.continueWithoutLotText"
        :cancel-process-text="lotResolvePopup.cancelProcessText"
        :show-continue-without-lot="false"
        @resolved="onLotResolvePopupResolved"        
      />
      <WeighingLotItemsPopup
        :visible="weighingLotItemsPopup.visible"
        :mode="weighingLotItemsPopup.mode"
        :product-name="weighingLotItemsPopup.productName"
        :measure-unit-symbol="weighingLotItemsPopup.measureUnitSymbol"
        :target-quantity="weighingLotItemsPopup.targetQuantity"
        :target-quantity-kg="weighingLotItemsPopup.targetQuantityKg"
        :conversion-factor-kg-per-unit="weighingLotItemsPopup.conversionFactorKgPerUnit"
        :initial-rows="weighingLotItemsPopup.rows"
        :epsilon="0.0001"
        @resolved="onWeighingLotItemsPopupResolved"
        @hidden="onWeighingLotItemsPopupHidden"
      />
      <ProductLotCategoryLabelPopup ref="productLotCategoryLabelPopup" />
      <ZeroWeightConfirmDialog
        :visible="zeroWeightConfirmPopup.visible"
        :title="zeroWeightConfirmPopup.title"
        :message="zeroWeightConfirmPopup.message"
        @confirm="onZeroWeightConfirmResolved(true)"
        @cancel="onZeroWeightConfirmResolved(false)"
      />
    </div>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxTabPanel from "devextreme-vue/tab-panel";
import settings from "./partials/settings.vue";
import scaling from "./partials/scaling.vue";
import load from "./partials/load.vue";
import additionalData from "./partials/additionalData.vue";
import note from "./partials/note.vue";
import wosForm from "./partials/wosForm.vue";
import LotNumberResolvePopup from "@/components/Common/LotNumberResolvePopup.vue";
import WeighingLotItemsPopup from "./partials/WeighingLotItemsPopup.vue";
import ProductLotCategoryLabelPopup from "./partials/ProductLotCategoryLabelPopup.vue";
import RightInfoPanel from "./partials/rightInfoPanel.vue";
import WarehouseSplitPopup from "./partials/WarehouseSplitPopup.vue";
import ZeroWeightConfirmDialog from "./partials/ZeroWeightConfirmDialog.vue";
import { canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import Forbidden from "@/pages/errors/Forbidden";
import StepsBar from "@/components/Common/StepsBar.vue";
import { successMessage, warningMessage, errorMessage, handlePromiseException } from "@/helpers/common";
import { confirm } from "devextreme/ui/dialog";
import Loader from "@/components/Loader";
import moment from "moment";
import EditCreateTitleBox from "@/components/Common/EditCreateTitleBox.vue";
import { FIELD_REPORT_MODES, WEIGHING_TYPES } from "@/pages/scale/config/weighingTypes";
import {
  roundToDecimals,
  divideWeightByParcels,
  extractProductFromWeighingProducts,
  extractParcelsFromRecord,
  extractRecipientWarehouseUuidsFromWeighingProducts,
} from "@/helpers/weighingOnScaleHelper";
import { mapAvailableLotGroups } from "~/helpers/stockHelper";

export default {
  name: "WeighingOnScaleAddOrEdit",
  data() {
    return {
      title: this.$t("scale.weighing_on_scale.edit_scale"),
      isLoading: false,
      isSaving: false,
      analyzerDataEnabled: false,
      fieldReportUsaAutomationEnabled: false,
      productLabelClassificationRequiredOnFieldReport: false,
      productLotCategoryLabels: [],
      productCacheByUuid: {},
      formData: {
        uuid: null,
        weighing_type_id: null,
        weighing_type: "",
        field_report_type: null,
        date: null,
        scale_house_uuid: null,
        scale_house_name: "",
        tractor_uuid: null,
        tractor_number_plate: "",
        trailer_uuid: null,
        trailer_number_plate: "",
        driver_uuid: null,
        driver_name: "",
        driver_serial_identifier: "",
        driver_number_identifier: "",
        vehicle_configuration: "",
        freight_carrier_uuid: null,
        freight_carrier_name: null,
        freight_carrier_address: null,
        unloading_place: null,
        uploading_place: null,
        sale_unloading_place: null,
        sale_uploading_place: null,
        //scaling
        empty_scale_uuid: null,
        empty_weight_kg: null,
        full_scale_uuid: null,
        full_weight_kg: null,
        empty_datetime: null,
        full_datetime: null,
        different_quantity_for_delivery: false,
        full_delivery_scale_uuid: null,
        full_delivery_weight_kg: null,
        full_delivery_datetime: null,
        measurements: [],
        //load
        product_uuid: null,
        product_name: null,
        product_code: null,
        product_lot_category_label_uuid: null,
        recipient_warehouse_uuid: null, // simple case: single warehouse
        recipient_warehouse_uuids: [], // multi-warehouse split case: all selected warehouses
        recipient_warehouse_name: null,
        issuer_warehouse_uuid: null,
        issuer_warehouse_name: null,
        partner_uuid: null,
        partner_name: null,
        partner_address: null,
        season_on_parcel_uuid: null,
        buyer_seller_uuid: null, // by warehouse
        buyer_seller_name: null,
        buyer_seller_address: null,
        selectedParcels: [],
        field_report: false, // used for mobile app, to indicate if this is a field report
        contract_number: null,
        custody: null,
        selectedContracts: [],
        selectedSaleContracts: [],
        intermediary_warehouse_uuid: null,
        intermediary_warehouse_name: "",
        //additional data
        notes: null,
        additional_data: {},
        //form
        view_scale_ticket: false,
        create_inventory_movement: false,
        generate_delivery_note: false,
        has_stock_movement: 0,
        parent_uuid: null,
      },
      lotResolvePopup: {
        visible: false,
        title: "",
        message: "",
        mode: "select",
        options: [],
        inputPlaceholder: "",
        confirmText: "",
        continueWithoutLotText: "",
        cancelProcessText: "",
      },
      lotResolveResolver: null,
      weighingLotItemsPopup: {
        visible: false,
        mode: "reception",
        productName: "",
        measureUnitSymbol: "",
        targetQuantity: 0,
        targetQuantityKg: 0,
        conversionFactorKgPerUnit: 1,
        rows: [],
      },
      weighingLotItemsResolver: null,
      zeroWeightConfirmPopup: {
        visible: false,
        title: "",
        message: "",
      },
      zeroWeightConfirmResolver: null,
      steps: this.getStepComponents(this.analyzerDataEnabled), // Initialize with default steps
      currentStep: 1, // Set the current active step
    };
  },
  computed: {
    ...mapGetters({
      quantityDecimalPlaces: "settings/getQuantityDecimalPlaces",
      getDefaultDateFormat: "settings/getDefaultDateFormat",
      warehouses: "smWarehouses/smWarehouses",
    }),
    config() {
      return {
        permission: permissions.WEIGHING_ON_SCALE,
        permission_type: permissionTypes.access,
      };
    },
    canEdit() {
      return canEdit(this);
    },
  },
  components: {
    DxTabPanel,
    settings,
    scaling,
    load,
    additionalData,
    wosForm,
    LotNumberResolvePopup,
    WeighingLotItemsPopup,
    ProductLotCategoryLabelPopup,
    RightInfoPanel,
    WarehouseSplitPopup,
    ZeroWeightConfirmDialog,
    Forbidden,
    StepsBar,
    Loader,
    note,
    EditCreateTitleBox,
  },
  watch: {
    analyzerDataEnabled(newValue, oldValue) {
      // Update steps when analyzerDataEnabled changes
      this.steps = this.getStepComponents(newValue);
    },
  },
  methods: {
    getStepComponents(analyzerDataEnabled = false) {
      return [
        {
          number: 1,
          component: "settings",
          label: this.$t("common.settings"),
        },
        {
          number: 2,
          component: "scaling",
          label: this.$t("scale.weighing_on_scale.scaling"),
        },
        {
          number: 3,
          component: "load",
          label: this.$t("scale.weighing_on_scale.load"),
        },
        analyzerDataEnabled
          ? {
              number: 4,
              component: "additionalData",
              label: this.$t("common.additional_data"),
            }
          : null,
        {
          number: analyzerDataEnabled ? 5 : 4,
          component: "note",
          label: this.$t("remark"),
        },
        {
          number: analyzerDataEnabled ? 6 : 5,
          component: "wosForm",
          label: this.$t("scale.weighing_on_scale.form"),
        },
      ].filter((step) => step !== null);
    },
    close() {},
    ...mapActions({
      createWeighingOnScale: "scale/createWeighingOnScale",
      updateWeighingOnScale: "scale/updateWeighingOnScale",
      getWeighingOnScaleByUuid: "scale/getWeighingOnScaleByUuid",
      getWarehouses: "smWarehouses/getSmWarehouses",
      getSmProduct: "smProducts/getSmProduct",
      getPredefinedLotNumbers: "smPredefinedLotNumbers/getPredefinedLotNumbers",
      getSmStockMovementItemsAvailableSum: "smStocks/getSmStockMovementItemsAvailableSum",
      downloadPdf: "scale/downloadPdf",
      getGroupByCode: "settings/getGroupByCode",
      getQuantityWarningFulfilled: "scaleQuantityWarnings/getQuantityWarningFulfilled",
      getLabels: "labelManagement/getLabels",
    }),
    handleAddSave(dataToSave) {
      this.createWeighingOnScale(dataToSave)
        .then((response) => {
          if (dataToSave.view_scale_ticket) {
            this.handleDownloadPdf(response.data);
          }
          this.showSaveResultMessage(response.message);
          this.$router.push({ name: "weighing-on-scale" });
        })
        .catch((e) => {
          handlePromiseException(this, e);
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    handleEditSave(dataToSave) {
      this.updateWeighingOnScale(dataToSave)
        .then((response) => {
          if (dataToSave.view_scale_ticket) {
            this.handleDownloadPdf(response.data);
          }
          this.showSaveResultMessage(response.message);
          this.$router.push({ name: "weighing-on-scale" });
        })
        .catch((e) => {
          handlePromiseException(this, e);
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    // sales stock movement was skipped after the weighing/reception were already saved
    showSaveResultMessage(message) {
      if (message === "SALE_STOCK_INSUFFICIENT") {
        warningMessage(this, this.$t("scale.weighing_on_scale.warning_sale_stock_insufficient"));
      } else {
        successMessage(this, message);
      }
    },
    async fetchWeighingOnScaleData(uuid, resetSpecialFields = false) {
      try {
        const response = await this.getWeighingOnScaleByUuid({
          uuid: uuid,
        });
        if (response && response.data) {
            const field_report = response?.data?.field_report ?? false;
            const isTransportMode =
              parseInt(response?.data?.field_report_type) === FIELD_REPORT_MODES.WEIGHING_FOR_TRANSPORT;
            if (field_report) {
              response.data.create_inventory_movement = !isTransportMode;
            }

            if (resetSpecialFields) {
              response.data.uuid = null;
              response.data.parent_uuid = uuid;
              // Reset fields that should not carry over from the source
              response.data.has_stock_movement = 0;
              response.data.finished_weighing = 0;
              response.data.scale_house_uuid = null;
              response.data.scale_house_name = "";
              response.data.measurements = [];
              response.data.empty_scale_uuid = null;
              response.data.empty_weight_kg = null;
              response.data.empty_datetime = null;
              response.data.full_scale_uuid = null;
              response.data.full_weight_kg = null;
              response.data.full_datetime = null;
              response.data.different_quantity_for_delivery = false;
              response.data.full_delivery_scale_uuid = null;
              response.data.full_delivery_weight_kg = null;
              response.data.full_delivery_datetime = null;
              response.data.netto_weight_kg = null;
            }

            response.data.date = moment(response.data.date).format(this.getDefaultDateFormat);

            // Restore product identity and warehouse routing from weighing_products.
            // recipient_warehouse_uuid and issuer_warehouse_uuid are only stored
            // inside weighing_products; they are not flat fields on the record.
            const wpFields = extractProductFromWeighingProducts(response.data);
            if (wpFields.product_uuid) {
              response.data.product_uuid = wpFields.product_uuid;
              response.data.product_name = wpFields.product_name;
              response.data.product_code = wpFields.product_code;
              response.data.product_lot_category_label_uuid = wpFields.label_uuid || null;
            }
            // Collect every unique recipient warehouse UUID from all weighing_products
            // entries so that multi-warehouse splits are fully restored.
            // recipient_warehouse_uuid  = first warehouse (primary / single-warehouse case).
            // recipient_warehouse_uuids = all distinct warehouses (multi-warehouse split).
            const recipientWarehouseUuids = extractRecipientWarehouseUuidsFromWeighingProducts(response.data);
            if (recipientWarehouseUuids.length > 0) {
              response.data.recipient_warehouse_uuid  = recipientWarehouseUuids[0];
              response.data.recipient_warehouse_uuids = recipientWarehouseUuids;
            }
            if (wpFields.issuer_warehouse_uuid) {
              response.data.issuer_warehouse_uuid = wpFields.issuer_warehouse_uuid;
            }

            // Ensure selectedParcels is populated from the API response.
            if (!Array.isArray(response.data.selectedParcels) || response.data.selectedParcels.length === 0) {
              response.data.selectedParcels = extractParcelsFromRecord(response.data);
            }

            // Update parent formData with fetched data and keep additional_data object-shaped.
            this.formData = {
              ...this.formData,
              ...response.data,
              additional_data: response?.data?.additional_data || this.formData.additional_data || {},
            };

            for (const step of this.steps) {
              const stepComponent = this.$refs[step.component];
              if (stepComponent && typeof stepComponent.updateData === "function") {
                if (step.number === 1) {
                  stepComponent.updateData(response.data);
                } else {
                  setTimeout(() => {
                    stepComponent.updateData(response.data);
                  }, step.number * 10);
                }
              }
            }
          }
      } finally {
        this.isLoading = false;
      }
    },
    handleStepClick(stepNumber) {
      // Check if the step is valid
      if (stepNumber < 1 || stepNumber > this.steps.length) {
        return;
      }

      if (stepNumber > this.currentStep) {
        const currentStepComponent = this.$refs[this.steps[this.currentStep - 1].component];
        if (currentStepComponent && typeof currentStepComponent.validate === "function") {
          const valid = currentStepComponent.validate();
          if (valid.error) {
            errorMessage(this, valid.message || this.$t("fill_out_error"));
            return;
          }
        }
      }

      this.currentStep = stepNumber;
    },
    onStepChanged(e) {
      this.currentStep = e.addedItems[0].number;
    },
    updateFormData(updatedData, stepNum) {
      // Merge updated data into formData
      this.formData = { ...this.formData, ...updatedData };

      // Sync updated formData to all other step components (except the one that emitted)
      for (const step of this.steps) {
        const stepComponent = this.$refs[step.component];
        if (stepComponent && typeof stepComponent.updateData === "function") {
          // Only update if not the step that just emitted the change
          if (step.number !== stepNum) {
            stepComponent.updateData(this.formData);
          }
        }
      }
    },
    /**
     * Main save handler for a weighing-on-scale record (create or update).
     *
     * Execution flow:
     *  1. Guards against double-submit (isSaving flag) and editing records that
     *     already have a confirmed stock movement (has_stock_movement).
     *  2. Validates every wizard step sequentially via each step component's
     *     `validate()` method — aborts on the first error.
     *  3. Builds a flat `dataToSave` snapshot from `formData`, resolving
     *     contract UUIDs and removing the local `selectedContracts` /
     *     `selectedSaleContracts` arrays before sending to the API.
     *  4. Constructs the `measurements` array from the individual empty- and
     *     full-weight fields.  If either weight is missing / zero the
     *     inventory-movement and scale-ticket flags are cleared.
     *  5. Calculates `netto_weight_kg = full_weight_kg - empty_weight_kg`.
     *     Returns an error if the result is <= 0.
     *  6. For FIELD_REPORT weighing types that require a netto weight
     *     (WITH_SALE, WITH_SALE_AND_INTERMEDIARY sub-modes) an additional
     *     guard is applied.
     *  7. Resolves the list of recipient warehouse UUIDs and, when the record
     *     is a simple field-report without a contract AND more than one
     *     warehouse is selected, opens the `WarehouseSplitPopup` so the
     *     operator can confirm how the netto weight is distributed across
     *     warehouses.  Cancelling the dialog aborts the save.
     *  8. Calls `getProductDivision` to build the `weighing_products` payload
     *     — either a surface-proportional split per parcel or a single entry.
     *  9. For FIELD_REPORT types, falls back to `buyer_seller_name` /
     *     `buyer_seller_address` when `partner_name` is missing.
     * 10. Checks the quantity-warning limit via `getQuantityWarningFulfilled`
     *     and, when the projected total would exceed the configured limit,
     *     shows a DevExtreme confirmation dialog.  A "cancel" response stops
     *     the save; API errors during the check are silently ignored so they
     *     do not block legitimate saves.
     * 11. Dispatches either `createWeighingOnScale` (new) or
     *     `updateWeighingOnScale` (edit) and redirects to the list on success.
     *     For new FIELD_REPORT records, `field_report` is set to `true`.
     *
     * @async
     * @returns {Promise<void>}
     */
    async save() {
      if (this.isSaving) {
        return;
      }

      // Ensure latest Additional Data values are present even if tab watcher did not emit yet.
      const additionalDataStep = this.$refs.additionalData;
      if (additionalDataStep?.localData?.additional_data) {
        this.formData.additional_data = {
          ...(this.formData.additional_data || {}),
          ...additionalDataStep.localData.additional_data,
        };
      } else if (!this.formData.additional_data) {
        this.formData.additional_data = {};
      }

      if (this.formData.has_stock_movement) {
        errorMessage(this, this.$t("scale.weighing_on_scale.error_edit_with_stock_movement"));
        return;
      }

      for (const step of this.steps) {
        const stepComponent = this.$refs[step.component];
        if (stepComponent && typeof stepComponent.validate === "function") {
          const valid = stepComponent.validate();
          if (valid.error) {
            errorMessage(this, valid.message || this.$t("fill_out_error"));
            return;
          }
        }
      }

      const dataToSave = {
        ...this.formData,
      };

      dataToSave.contract_uuid = dataToSave.selectedContracts?.[0]?.uuid || null;
      dataToSave.sale_contract_uuid = dataToSave.selectedSaleContracts?.[0]?.uuid || null;
      //delete dataToSave.selectedContracts;
      //delete dataToSave.selectedSaleContracts;

      dataToSave.measurements = [];
      // add empty and full weights to measurements
      if ((dataToSave.empty_weight_kg ?? 0) >= 0 && (dataToSave.empty_datetime)) {
        dataToSave.measurements.push({
          scale_uuid: dataToSave.empty_scale_uuid,
          datetime: dataToSave.empty_datetime,
          weight_kg: dataToSave.empty_weight_kg ?? 0,
          measurement_type: "EMPTY",
        });
      } else {
        dataToSave.empty_weight_kg = 0;
      }

      if ((dataToSave.full_weight_kg ?? 0) > 0) {
        dataToSave.measurements.push({
          scale_uuid: dataToSave.full_scale_uuid,
          datetime: dataToSave.full_datetime,
          weight_kg: dataToSave.full_weight_kg,
          measurement_type: "FULL",
        });
      } else {
        dataToSave.full_weight_kg = 0;
      }

      // delivery weight only applies to FIELD_REPORT / WITH_SALE with the override checkbox enabled
      const isFieldReportWithSale =
        parseInt(dataToSave.weighing_type_id) === WEIGHING_TYPES.FIELD_REPORT &&
        parseInt(dataToSave.field_report_type) === FIELD_REPORT_MODES.WITH_SALE;

      if (!isFieldReportWithSale) {
        dataToSave.different_quantity_for_delivery = false;
      }

      if (isFieldReportWithSale && dataToSave.different_quantity_for_delivery && !((dataToSave.full_delivery_weight_kg ?? 0) > 0)) {
        errorMessage(this, this.$t("scale.weighing_on_scale.error_delivery_weight_required"));
        return;
      }

      if (dataToSave.different_quantity_for_delivery && (dataToSave.full_delivery_weight_kg ?? 0) > 0) {
        dataToSave.measurements.push({
          scale_uuid: dataToSave.full_delivery_scale_uuid,
          datetime: dataToSave.full_delivery_datetime,
          weight_kg: dataToSave.full_delivery_weight_kg,
          measurement_type: "FULL_DELIVERY",
        });
      } else {
        dataToSave.different_quantity_for_delivery = false;
        dataToSave.full_delivery_scale_uuid = null;
        dataToSave.full_delivery_weight_kg = 0;
        dataToSave.full_delivery_datetime = null;
      }
      //-----------------------------------------------

      const areTareAndGrossZero = (dataToSave.empty_weight_kg ?? 0) === 0 && (dataToSave.full_weight_kg ?? 0) === 0;
      if (areTareAndGrossZero) {
        const shouldContinue = await this.openZeroWeightConfirmDialog();

        if (!shouldContinue) {
          return;
        }
      }

      // calculate netto weight
      dataToSave.netto_weight_kg = 0;
      if ((dataToSave.full_weight_kg ?? 0) > 0 && ((dataToSave.empty_weight_kg ?? 0) >= 0 && dataToSave.empty_datetime)) {
        dataToSave.netto_weight_kg = (dataToSave.full_weight_kg ?? 0) - (dataToSave.empty_weight_kg ?? 0);
        if (dataToSave.netto_weight_kg <= 0) {
          errorMessage(this, this.$t("scale.weighing_on_scale.error_netto_weight"));
          return;
        }
      } else {
        dataToSave.create_inventory_movement = false;
        dataToSave.view_scale_ticket = false;
      }

      /*const requiresNettoWeight =
        parseInt(dataToSave.weighing_type_id) === WEIGHING_TYPES.FIELD_REPORT &&
        [FIELD_REPORT_MODES.WITH_SALE, FIELD_REPORT_MODES.WITH_SALE_AND_INTERMEDIARY].includes(parseInt(dataToSave.field_report_type));
      if (requiresNettoWeight && (dataToSave.netto_weight_kg ?? 0) <= 0) {
        errorMessage(this, this.$t("scale.weighing_on_scale.error_netto_weight_field_report"));
        return;
      }*/

      // no inventory movements when weighing type is 5
      if (dataToSave.weighing_type_id == WEIGHING_TYPES.CONTRACTED_MEASUREMENTS) {
        dataToSave.create_inventory_movement = false;
      }

      if (
        parseInt(dataToSave.weighing_type_id) === WEIGHING_TYPES.FIELD_REPORT &&
        parseInt(dataToSave.field_report_type) === FIELD_REPORT_MODES.WEIGHING_FOR_TRANSPORT
      ) {
        dataToSave.create_inventory_movement = false;
      }

      if (parseInt(dataToSave.weighing_type_id) === WEIGHING_TYPES.TRANSFER) {
        if (!dataToSave.issuer_warehouse_uuid || !dataToSave.recipient_warehouse_uuid) {
          errorMessage(this, this.$t("fill_out_error"));
          return;
        }
      }

      const selectedRecipientWarehouseUuids = this.getSelectedRecipientWarehouseUuids(dataToSave);
      dataToSave.recipient_warehouse_uuids = selectedRecipientWarehouseUuids;
      dataToSave.recipient_warehouse_uuid = selectedRecipientWarehouseUuids[0] || null;

      let splitRows = null;
      if (
        this.isSimpleFieldReportWithoutContract(dataToSave) &&
        selectedRecipientWarehouseUuids.length > 1
      ) {
        // Always compute the warehouse/parcel split so that weighing_products
        // contains correct per-warehouse entries regardless of whether the
        // netto weight is known yet.
        const splitInit = this.prepareWarehouseSplitRows(dataToSave, dataToSave.netto_weight_kg);
        if (splitInit.error) {
          errorMessage(this, splitInit.message || this.$t("fill_out_error"));
          return;
        }

        if ((dataToSave.netto_weight_kg ?? 0) > 0) {
          // Weight is known — let the operator review and confirm the distribution.
          splitRows = await this.openWarehouseSplitDialog({
            rows: splitInit.rows,
            targetTotal: dataToSave.netto_weight_kg,
            expectedLegalEntityUuids: splitInit.expectedLegalEntityUuids,
          });

          if (!splitRows) {
            return; // Operator cancelled the dialog.
          }
        } else {
          // Weight not yet determined (unfinished weighing) — use the auto-computed
          // rows directly (all quantities will be 0) so that warehouse / parcel /
          // product associations are still present in the weighing_products payload.
          splitRows = splitInit.rows;
        }
      }

      const canContinueProductLabelClassification = await this.resolveProductLabelClassificationBeforeSave(dataToSave);
      if (canContinueProductLabelClassification === false) {
        return;
      }

      // divide netto weight by parcel surface or split by selected warehouses
      dataToSave.weighing_products = this.getProductDivision(dataToSave.netto_weight_kg, {
        formData: dataToSave,
        splitRows,
      });

      this.mergeExistingLotNumbersFromSavedWeighing(dataToSave);
      const canContinueSave = await this.resolveFieldReportLotNumberBeforeSave(dataToSave);
      if (canContinueSave === false) {
        return;
      }

      const canContinueLotItemsSave = await this.resolveReceptionSalesLotItemsBeforeSave(dataToSave);
      if (canContinueLotItemsSave === false) {
        return;
      }

      this.fillBuyerSellerFromLargestParcel(dataToSave);

      // if reception type is surface
      if (
        dataToSave.weighing_type_id == WEIGHING_TYPES.FIELD_REPORT &&
        (!(dataToSave.field_report ?? false) || dataToSave.partner_name == null || dataToSave.partner_name === "")
      ) {
        dataToSave.partner_name = dataToSave.buyer_seller_name;
        dataToSave.partner_address = dataToSave.buyer_seller_address;
      }

      // Check quantity warning limit before saving
      if ((dataToSave.netto_weight_kg ?? 0) > 0 && dataToSave.product_uuid && dataToSave.buyer_seller_uuid) {
        const year = dataToSave.date ? moment(dataToSave.date, this.getDefaultDateFormat).year() : null;
        if (year) {
          try {
            const warningRes = await this.getQuantityWarningFulfilled({
              department_uuid: dataToSave.buyer_seller_uuid,
              product_uuid: dataToSave.product_uuid,
              year: year,
              weighing_type_id: dataToSave.weighing_type_id,
            });
            const warningData = warningRes?.data;
            if (warningData?.has_limit) {
              const factor = warningData.kg_to_unit_factor ?? 1;
              const weighingQty = (dataToSave.netto_weight_kg ?? 0) / factor;
              const projected = (warningData.fulfilled_quantity ?? 0) + weighingQty;
              if (projected > (warningData.quantity_limit ?? 0)) {
                const shouldContinue = await confirm(
                  this.$t("scale.quantity_warnings.limit_exceed_confirm_message", {
                    product: dataToSave.product_name ?? "",
                    fulfilled: (warningData.fulfilled_quantity ?? 0).toFixed(2),
                    weighing: weighingQty.toFixed(2),
                    limit: (warningData.quantity_limit ?? 0).toFixed(2),
                    unit: warningData.measure_unit ?? "",
                  }),
                  this.$t("scale.quantity_warnings.limit_exceed_confirm_title"),
                );
                if (!shouldContinue) {
                  return;
                }
              }
            }
          } catch (e) {
            // Do not block save if the check fails
            console.warn("Quantity warning check failed:", e);
          }
        }
      }

      if (dataToSave.uuid) {
        // in edit mode do not change field report status
        this.isSaving = true;
        this.handleEditSave(dataToSave);
      } else {
        //dataToSave.field_report = false; // set field report to false, because this is not a field report, field report coming just from mobile screen -- deprecated
        if (dataToSave.weighing_type_id == WEIGHING_TYPES.FIELD_REPORT) {
          dataToSave.field_report = true; // set field report to true, because this is surface type, and we need to identify it as a field report
        }
        this.isSaving = true;
        this.handleAddSave(dataToSave);
      }
    },
    isSimpleFieldReportWithoutContract(data = this.formData) {
      return (
        parseInt(data?.weighing_type_id) === WEIGHING_TYPES.FIELD_REPORT &&
        ([FIELD_REPORT_MODES.SIMPLE, FIELD_REPORT_MODES.WEIGHING_FOR_TRANSPORT].includes(parseInt(data?.field_report_type)) ||
          !data?.field_report_type) &&
        (!Array.isArray(data?.selectedContracts) || data.selectedContracts.length === 0)
      );
    },
    getSelectedRecipientWarehouseUuids(data = this.formData) {
      if (Array.isArray(data?.recipient_warehouse_uuids) && data.recipient_warehouse_uuids.length > 0) {
        return data.recipient_warehouse_uuids.filter((uuid) => !!uuid);
      }

      if (data?.recipient_warehouse_uuid) {
        return [data.recipient_warehouse_uuid];
      }

      return [];
    },
    getParcelLegalEntityUuid(parcel) {
      return parcel?.legal_entity?.uuid ?? parcel?.department_uuid ?? null;
    },
    getWarehouseLegalEntityUuid(warehouse) {
      return warehouse?.legal_entity_uuid ?? warehouse?.department?.uuid ?? null;
    },
    fillBuyerSellerFromLargestParcel(dataToSave) {
      const isTransportMode =
        parseInt(dataToSave?.weighing_type_id) === WEIGHING_TYPES.FIELD_REPORT &&
        parseInt(dataToSave?.field_report_type) === FIELD_REPORT_MODES.WEIGHING_FOR_TRANSPORT;

      if (!isTransportMode) {
        return;
      }

      const selectedRecipientWarehouseUuids = this.getSelectedRecipientWarehouseUuids(dataToSave);
      if (selectedRecipientWarehouseUuids.length > 0) {
        return; // buyer_seller already set via warehouse
      }

      const parcels = Array.isArray(dataToSave?.selectedParcels) ? dataToSave.selectedParcels : [];
      if (parcels.length === 0) {
        return;
      }

      // Find parcel with biggest surface
      let largestParcel = parcels[0];
      let maxSurface = Number(parcels[0]?.surface) || 0;

      for (let i = 1; i < parcels.length; i++) {
        const surface = Number(parcels[i]?.surface) || 0;
        if (surface > maxSurface) {
          maxSurface = surface;
          largestParcel = parcels[i];
        }
      }

      // Extract legal entity name and address, fallback to department
      const legalEntity = largestParcel?.legal_entity || largestParcel?.department;
      const legalEntityName = legalEntity?.name;
      const legalEntityAddress = legalEntity?.address;

      if (legalEntityName && !dataToSave.buyer_seller_name) {
        dataToSave.buyer_seller_name = legalEntityName;
        dataToSave.partner_name = legalEntityName;
      }

      if (legalEntityAddress && !dataToSave.buyer_seller_address) {
        dataToSave.buyer_seller_address = legalEntityAddress;
        dataToSave.partner_address = legalEntityAddress;
      }
    },
    /**
     * Groups an array of parcels by their owning legal entity (department).
     *
     * Iterates over every parcel and resolves its legal-entity UUID via
     * `getParcelLegalEntityUuid`.  Parcels without a resolvable UUID are
     * silently skipped.  For each unique legal-entity UUID a group object is
     * created that accumulates:
     *  - `legal_entity_uuid`  – the UUID of the owning legal entity.
     *  - `legal_entity_name`  – display name, resolved from
     *                           `parcel.legal_entity.name` → `parcel.department.name` → "-".
     *  - `parcels`            – all parcel objects belonging to this entity.
     *  - `total_surface`      – sum of `parcel.surface` values (numeric, 0 if missing).
     *
     * The result is used downstream by `prepareWarehouseSplitRows` and
     * `buildProductDivisionFromWarehouseSplit` to proportionally distribute
     * netto weight across parcels / warehouses on a per-legal-entity basis.
     *
     * @param {Object[]} [parcels=[]] - Array of parcel objects from `formData.selectedParcels`.
     * @returns {Object.<string, {legal_entity_uuid: string, legal_entity_name: string, parcels: Object[], total_surface: number}>}
     *   A plain object keyed by legal-entity UUID, each value being the
     *   aggregated group descriptor.
     */
    buildLegalEntityParcelGroups(parcels = []) {
      const groups = {};
      parcels.forEach((parcel) => {
        const legalEntityUuid = this.getParcelLegalEntityUuid(parcel);
        if (!legalEntityUuid) {
          return;
        }

        if (!groups[legalEntityUuid]) {
          groups[legalEntityUuid] = {
            legal_entity_uuid: legalEntityUuid,
            legal_entity_name: parcel?.legal_entity?.name || parcel?.department?.name || "-",
            parcels: [],
            total_surface: 0,
          };
        }

        const surface = Number(parcel?.surface) || 0;
        groups[legalEntityUuid].parcels.push(parcel);
        groups[legalEntityUuid].total_surface += surface;
      });

      return groups;
    },
    /**
     * Computes the initial warehouse-split rows that are presented to the
     * operator inside the `WarehouseSplitPopup` before they confirm the save.
     *
     * This method is called only when ALL of the following conditions hold:
     *  - The weighing type is FIELD_REPORT (simple sub-mode, no contract).
     *  - `netto_weight_kg` is positive.
     *  - More than one recipient warehouse is selected.
     *
     * Algorithm:
     *  1. Resolves the selected warehouse objects from the Vuex `warehouses`
     *     store using the UUID list from `formData`.
     *  2. Groups the selected parcels by legal entity via
     *     `buildLegalEntityParcelGroups`.
     *  3. Validates that every parcel legal entity has at least one matching
     *     warehouse assigned, and vice-versa — returns an error object if not.
     *  4. Computes each legal-entity group's proportional share of
     *     `nettoWeightKg` based on its total parcel surface relative to the
     *     grand total surface.  The last group receives the remainder to avoid
     *     floating-point accumulation drift.
     *  5. Within each group, distributes that group's quantity equally among
     *     its warehouses.  Again, the last warehouse in the group absorbs any
     *     remainder.
     *  6. Returns a flat array of row objects (`rows`) that can be displayed
     *     and edited in the split popup, plus `expectedLegalEntityUuids` for
     *     validation inside the popup component.
     *
     * @param {Object} dataToSave  - The form data snapshot being prepared for save.
     * @param {number} nettoWeightKg - The calculated net weight in kg.
     * @returns {{ error: true, message: string }
     *          |{ error: false, rows: Object[], expectedLegalEntityUuids: string[] }}
     *   Returns an error descriptor on validation failure, or a success
     *   descriptor with the computed split rows and expected legal-entity UUIDs.
     */
    prepareWarehouseSplitRows(dataToSave, nettoWeightKg) {
      const recipientWarehouseUuids = this.getSelectedRecipientWarehouseUuids(dataToSave);
      const selectedWarehouses = recipientWarehouseUuids
        .map((uuid) => this.warehouses.find((warehouse) => warehouse.uuid === uuid))
        .filter((warehouse) => !!warehouse);

      const parcelGroups = this.buildLegalEntityParcelGroups(dataToSave?.selectedParcels || []);
      const parcelLegalEntityUuids = Object.keys(parcelGroups);

      if (parcelLegalEntityUuids.length === 0) {
        return {
          error: true,
          message: this.$t("fill_out_error"),
        };
      }

      const warehouseByLegalEntity = {};
      selectedWarehouses.forEach((warehouse) => {
        const legalEntityUuid = this.getWarehouseLegalEntityUuid(warehouse);
        if (!legalEntityUuid) {
          return;
        }
        if (!warehouseByLegalEntity[legalEntityUuid]) {
          warehouseByLegalEntity[legalEntityUuid] = [];
        }
        warehouseByLegalEntity[legalEntityUuid].push(warehouse);
      });

      const missingWarehouseForParcelEntity = parcelLegalEntityUuids.some((uuid) => !warehouseByLegalEntity[uuid]?.length);
      if (missingWarehouseForParcelEntity) {
        return {
          error: true,
          message: this.$t("works.parcels.parcel_warehouse_department_error"),
        };
      }

      const warehouseEntityUuids = Object.keys(warehouseByLegalEntity);
      const hasWarehouseWithoutParcelEntity = warehouseEntityUuids.some((uuid) => !parcelGroups[uuid]);
      if (hasWarehouseWithoutParcelEntity) {
        return {
          error: true,
          message: this.$t("works.parcels.parcel_warehouse_department_error"),
        };
      }

      const totalSurface = parcelLegalEntityUuids.reduce((sum, uuid) => sum + (parcelGroups[uuid].total_surface || 0), 0);
      if (totalSurface <= 0) {
        return {
          error: true,
          message: this.$t("fill_out_error"),
        };
      }

      const rows = [];
      let assignedTotal = 0;
      const groupCount = parcelLegalEntityUuids.length;

      parcelLegalEntityUuids.forEach((legalEntityUuid, groupIndex) => {
        const group = parcelGroups[legalEntityUuid];
        const groupSurface = group.total_surface || 0;
        const warehouses = warehouseByLegalEntity[legalEntityUuid] || [];

        let groupQuantity = 0;
        if (groupIndex < groupCount - 1) {
          groupQuantity = this.roundToDecimals(((groupSurface / totalSurface) * (nettoWeightKg || 0)), this.quantityDecimalPlaces);
          assignedTotal += groupQuantity;
        } else {
          groupQuantity = this.roundToDecimals((nettoWeightKg || 0) - assignedTotal, this.quantityDecimalPlaces);
        }

        let groupWarehouseAssigned = 0;
        warehouses.forEach((warehouse, warehouseIndex) => {
          let quantity = 0;
          if (warehouseIndex < warehouses.length - 1) {
            quantity = this.roundToDecimals(groupQuantity / warehouses.length, this.quantityDecimalPlaces);
            groupWarehouseAssigned += quantity;
          } else {
            quantity = this.roundToDecimals(groupQuantity - groupWarehouseAssigned, this.quantityDecimalPlaces);
          }

          rows.push({
            row_key: `${legalEntityUuid}-${warehouse.uuid}`,
            legal_entity_uuid: legalEntityUuid,
            legal_entity_name: group.legal_entity_name || warehouse?.legal_entity_name || warehouse?.department?.name || "-",
            warehouse_uuid: warehouse.uuid,
            warehouse_name: warehouse.name,
            quantity_kg: quantity,
            parcel_names: (group.parcels || []).map((p) => p.name || "-").join(", "),
            total_surface: group.total_surface || 0,
          });
        });
      });

      return {
        error: false,
        rows,
        expectedLegalEntityUuids: parcelLegalEntityUuids,
      };
    },
    openWarehouseSplitDialog(payload) {
      return this.$refs.warehouseSplitPopup?.open(payload) || Promise.resolve(null);
    },
    mergeExistingLotNumbersFromSavedWeighing(dataToSave) {
      if (!Array.isArray(dataToSave?.weighing_products) || !Array.isArray(this.formData?.weighing_products)) {
        return;
      }

      const lotMapByProductParcel = {};
      const fallbackLotByProduct = {};

      this.formData.weighing_products.forEach((item) => {
        const lotNumber = (item?.lot_number || "").trim();
        if (!lotNumber) {
          return;
        }

        const productUuid = item?.product_uuid || "";
        const parcelUuid = item?.parcel_uuid || "";
        if (productUuid && parcelUuid) {
          lotMapByProductParcel[`${productUuid}|${parcelUuid}`] = lotNumber;
        }
        if (productUuid && !fallbackLotByProduct[productUuid]) {
          fallbackLotByProduct[productUuid] = lotNumber;
        }
      });

      dataToSave.weighing_products = dataToSave.weighing_products.map((item) => {
        const existingLot = (item?.lot_number || "").trim();
        if (existingLot) {
          return item;
        }

        const productUuid = item?.product_uuid || "";
        const parcelUuid = item?.parcel_uuid || "";
        const mappedLot = lotMapByProductParcel[`${productUuid}|${parcelUuid}`] || fallbackLotByProduct[productUuid] || "";

        return mappedLot ? { ...item, lot_number: mappedLot } : item;
      });
    },
    async resolveFieldReportLotNumberBeforeSave(dataToSave) {
      if (parseInt(dataToSave?.weighing_type_id) !== WEIGHING_TYPES.FIELD_REPORT) {
        return true;
      }

      const weighingProducts = Array.isArray(dataToSave?.weighing_products) ? dataToSave.weighing_products : [];
      if (!weighingProducts.length) {
        return true;
      }

      if (weighingProducts.some((item) => !!(item?.lot_number || "").trim())) {
        return true;
      }

      const productUuid = weighingProducts[0]?.product_uuid;
      if (!productUuid) {
        return true;
      }

      const productData = await this.getCachedSmProduct(productUuid);
      if (!productData?.lot_management) {
        return true;
      }

      const parcelUuids = [...new Set(weighingProducts.map((item) => item?.parcel_uuid).filter((uuid) => !!uuid))];
      if (!parcelUuids.length) {
        return true;
      }

      const predefinedResponse = await this.getPredefinedLotNumbers({
        page: 1,
        limit: 1000,
        product_uuid: productUuid,
        parcel_uuids: parcelUuids.join(","),
      });

      const predefinedRows = predefinedResponse?.data?.data || [];
      const lotNumbers = [...new Set(predefinedRows.map((row) => (row?.lot_number || "").trim()).filter((value) => !!value))];

      let selectedLotNumber = "";
      let popupAction = "continue_without_lot";

      if (lotNumbers.length === 1) {
        selectedLotNumber = lotNumbers[0];
      } else if (lotNumbers.length > 1) {
        const popupResult = await this.openLotResolvePopup({
          title: this.$t("stock_management.predefined_lot_numbers.title"),
          message: this.$t("stock_management.products.lot_management"),
          mode: "select",
          options: lotNumbers,
        });
        popupAction = popupResult?.action || "continue_without_lot";
        selectedLotNumber = (popupResult?.value || "").trim();
      } else {
        const popupResult = await this.openLotResolvePopup({
          title: this.$t("lot_items.please_insert_lot_number"),
          message: this.$t("stock_management.predefined_lot_numbers.lot_number"),
          mode: "input",
          inputPlaceholder: this.$t("stock_management.predefined_lot_numbers.lot_number"),
        });
        popupAction = popupResult?.action || "continue_without_lot";
        selectedLotNumber = (popupResult?.value || "").trim();
      }

      if (popupAction === "cancel_save") {
        return false;
      }

      if (!selectedLotNumber) {
        return true;
      }

      dataToSave.weighing_products = weighingProducts.map((item) => ({
        ...item,
        lot_number: selectedLotNumber,
      }));
      return true;
    },
    openLotResolvePopup({ title, message, mode, options = [], inputPlaceholder = "" }) {
      return new Promise((resolve) => {
        this.lotResolveResolver = resolve;
        this.lotResolvePopup = {
          visible: true,
          title,
          message,
          mode,
          options,
          inputPlaceholder,
          confirmText: this.$t("common.save"),
          continueWithoutLotText: this.$t("stock_management.predefined_lot_numbers.save_without_lot_number"),
          cancelProcessText: this.$t("common.cancel"),
        };
      });
    },
    onLotResolvePopupResolved(payload) {
      const normalizedPayload = {
        action: payload?.action || "continue_without_lot",
        value: (payload?.value || "").trim(),
      };
      if (typeof this.lotResolveResolver === "function") {
        this.lotResolveResolver(normalizedPayload);
      }
      this.lotResolveResolver = null;
      this.lotResolvePopup.visible = false;
    },
    openZeroWeightConfirmDialog() {
      return new Promise((resolve) => {
        this.zeroWeightConfirmResolver = resolve;
        this.zeroWeightConfirmPopup = {
          visible: true,
          title: this.$t("scale.weighing_on_scale.zero_tare_gross_confirm_title"),
          message: this.$t("scale.weighing_on_scale.zero_tare_gross_confirm_message"),
        };
      });
    },
    onZeroWeightConfirmResolved(confirmed) {
      if (typeof this.zeroWeightConfirmResolver === "function") {
        this.zeroWeightConfirmResolver(!!confirmed);
      }
      this.zeroWeightConfirmResolver = null;
      this.zeroWeightConfirmPopup.visible = false;
    },
    async getCachedSmProduct(productUuid) {
      if (!productUuid) {
        return null;
      }

      if (Object.prototype.hasOwnProperty.call(this.productCacheByUuid, productUuid)) {
        return this.productCacheByUuid[productUuid];
      }

      const productData = await this.getSmProduct({ uuid: productUuid });
      this.productCacheByUuid = {
        ...this.productCacheByUuid,
        [productUuid]: productData || null,
      };

      return productData || null;
    },
    getKgConversionFactorForProduct(productData) {
      if (!productData) {
        return 1;
      }

      if (productData?.measure_unit?.symbol === "kg") {
        return 1;
      }

      const validMeasureUnits = Array.isArray(productData?.valid_measureunits_for_product)
        ? productData.valid_measureunits_for_product
        : [];
      const kgMapping = validMeasureUnits.find((item) => item?.target_measure_unit === "kg");
      const factor = parseFloat(kgMapping?.conversion_factor) || 0;
      return factor > 0 ? factor : 1;
    },
    convertKgToProductQuantity(nettoWeightKg, conversionFactorKgPerUnit) {
      const nettoKg = parseFloat(nettoWeightKg) || 0;
      const factor = parseFloat(conversionFactorKgPerUnit) || 1;
      if (factor <= 0) {
        return nettoKg;
      }
      return this.roundToDecimals(nettoKg / factor, 6);
    },
    async openWeighingLotItemsPopup(payload) {
      return new Promise((resolve) => {
        this.weighingLotItemsResolver = resolve;
        this.weighingLotItemsPopup = {
          visible: true,
          mode: payload.mode,
          productName: payload.productName || "",
          measureUnitSymbol: payload.measureUnitSymbol || "",
          targetQuantity: parseFloat(payload.targetQuantity) || 0,
          targetQuantityKg: parseFloat(payload.targetQuantityKg) || 0,
          conversionFactorKgPerUnit: parseFloat(payload.conversionFactorKgPerUnit) || 1,
          rows: Array.isArray(payload.rows) ? payload.rows : [],
        };
      });
    },
    onWeighingLotItemsPopupResolved(payload) {
      if (typeof this.weighingLotItemsResolver === "function") {
        this.weighingLotItemsResolver(payload || { action: "cancel_save", items: [] });
      }
      this.weighingLotItemsResolver = null;
      this.weighingLotItemsPopup.visible = false;
    },
    onWeighingLotItemsPopupHidden() {
      this.weighingLotItemsPopup.visible = false;
      if (typeof this.weighingLotItemsResolver === "function") {
        this.weighingLotItemsResolver({ action: "cancel_save", items: [] });
      }
      this.weighingLotItemsResolver = null;
    },
    async resolveReceptionSalesLotItemsBeforeSave(dataToSave) {
      const weighingTypeId = parseInt(dataToSave?.weighing_type_id);
      const isReceptionSalesOrTransfer = [WEIGHING_TYPES.RECEPTION, WEIGHING_TYPES.SALES, WEIGHING_TYPES.TRANSFER].includes(
        weighingTypeId,
      );
      if (!isReceptionSalesOrTransfer) {
        return true;
      }

      if (!(dataToSave?.create_inventory_movement ?? false)) {
        return true;
      }

      const nettoWeightKg = parseFloat(dataToSave?.netto_weight_kg) || 0;
      if (nettoWeightKg <= 0) {
        return true;
      }

      const weighingProducts = Array.isArray(dataToSave?.weighing_products) ? dataToSave.weighing_products : [];
      if (!weighingProducts.length) {
        return true;
      }

      const firstWeighingProduct = weighingProducts[0] || {};
      const productUuid = firstWeighingProduct?.product_uuid || dataToSave?.product_uuid;
      if (!productUuid) {
        return true;
      }

      const productData = await this.getCachedSmProduct(productUuid);
      if (!productData?.lot_management) {
        return true;
      }

      const conversionFactorKgPerUnit = this.getKgConversionFactorForProduct(productData);
      const targetQuantity = this.convertKgToProductQuantity(nettoWeightKg, conversionFactorKgPerUnit);
      const measureUnitSymbol = productData?.measure_unit?.symbol || "";

      const warehouseUuid = weighingTypeId === WEIGHING_TYPES.RECEPTION
        ? (firstWeighingProduct?.recipient_warehouse_uuid || dataToSave?.recipient_warehouse_uuid)
        : (firstWeighingProduct?.issuer_warehouse_uuid || dataToSave?.issuer_warehouse_uuid);

      if (!warehouseUuid) {
        errorMessage(this, this.$t("fill_out_error"));
        return false;
      }

      let popupRows = [];
      if ([WEIGHING_TYPES.SALES, WEIGHING_TYPES.TRANSFER].includes(weighingTypeId)) {
        const response = await this.getSmStockMovementItemsAvailableSum({
          product_uuid: productUuid,
          warehouse_uuid: warehouseUuid,
          movement_date: dataToSave.date,
          enablePagination: 0,
          limit: 99999,
        });
        const sourceRows = response?.data?.data || response?.data || [];
        popupRows = mapAvailableLotGroups(sourceRows).map((group) => ({
          ...group,
          quantity: 0,
        }));
      }

      const popupResult = await this.openWeighingLotItemsPopup({
        mode: weighingTypeId === WEIGHING_TYPES.RECEPTION ? "reception" : "sales",
        productName: firstWeighingProduct?.product_name || dataToSave?.product_name || productData?.name || "",
        measureUnitSymbol,
        targetQuantity,
        targetQuantityKg: nettoWeightKg,
        conversionFactorKgPerUnit,
        rows: popupRows,
      });

      if ((popupResult?.action || "cancel_save") !== "confirm") {
        return false;
      }

      const lotItems = Array.isArray(popupResult?.items) ? popupResult.items : [];
      if (!lotItems.length) {
        return false;
      }

      dataToSave.weighing_products = weighingProducts.map((item, idx) => ({
        ...item,
        lot_items: idx === 0 ? lotItems : [],
      }));

      return true;
    },
    /**
     * Converts the operator-confirmed warehouse split rows into the final
     * `weighing_products` array that is sent to the API.
     *
     * Called by `getProductDivision` when `options.splitRows` is non-empty
     * (i.e., the user went through the `WarehouseSplitPopup` flow).
     *
     * Algorithm:
     *  1. Re-groups the selected parcels by legal entity via
     *     `buildLegalEntityParcelGroups` so parcel surfaces are available.
     *  2. For each split row (one row = one legal entity + one warehouse):
     *     a. Locates the corresponding parcel group.
     *     b. Distributes the split row's `quantity_kg` proportionally across
     *        all parcels in the group by parcel surface.  The last parcel
     *        receives the remainder to prevent rounding loss.
     *     c. Pushes one product-division entry per parcel, associating it with
     *        the split row's warehouse UUID.
     *  3. After all rows are processed, sums the resulting `netto_weight_kg`
     *     values and compares with the target `nettoWeightKg`.
     *     Any rounding delta is added to the very last entry to guarantee the
     *     total matches exactly.
     *
     * Each entry in the returned array contains:
     *  - `product_uuid`, `product_name`, `product_code` – from `dataSource`.
     *  - `recipient_warehouse_uuid`                     – from the split row.
     *  - `issuer_warehouse_uuid`, `partner_uuid`        – from `dataSource`.
     *  - `season_on_parcel_uuid`, `parcel_uuid`, `surface` – from the parcel.
     *  - `netto_weight_kg`                              – proportional share.
     *  - `contract_uuid`                                – first selected contract or null.
     *
     * @param {Object[]} splitRows     - Confirmed rows from the WarehouseSplitPopup
     *                                   (each has `legal_entity_uuid`, `warehouse_uuid`,
     *                                   `quantity_kg`).
     * @param {number}   nettoWeightKg - The total net weight to distribute (kg).
     * @param {Object}  [dataSource]   - Form data source; defaults to `this.formData`.
     * @returns {Object[]} Array of weighing-product division entries for the API payload.
     */
    buildProductDivisionFromWarehouseSplit(splitRows, nettoWeightKg, dataSource = this.formData) {
      const productDivision = [];
      const parcelGroups = this.buildLegalEntityParcelGroups(dataSource?.selectedParcels || []);

      splitRows.forEach((splitRow) => {
        const legalEntityUuid = splitRow.legal_entity_uuid;
        const group = parcelGroups[legalEntityUuid];
        if (!group || !Array.isArray(group.parcels) || group.parcels.length === 0) {
          return;
        }

        const groupSurface = group.total_surface || 0;
        let assigned = 0;
        group.parcels.forEach((parcel, parcelIndex) => {
          let division = 0;
          if (parcelIndex < group.parcels.length - 1 && groupSurface > 0) {
            division = this.roundToDecimals(
              ((Number(parcel?.surface) || 0) / groupSurface) * (Number(splitRow.quantity_kg) || 0),
              this.quantityDecimalPlaces,
            );
            assigned += division;
          } else {
            division = this.roundToDecimals((Number(splitRow.quantity_kg) || 0) - assigned, this.quantityDecimalPlaces);
          }

          productDivision.push({
            product_uuid: dataSource.product_uuid,
            product_name: dataSource.product_name,
            product_code: dataSource.product_code,
            label_uuid: dataSource.product_lot_category_label_uuid || null,
            recipient_warehouse_uuid: splitRow.warehouse_uuid,
            issuer_warehouse_uuid: dataSource.issuer_warehouse_uuid,
            partner_uuid: dataSource.partner_uuid,
            season_on_parcel_uuid: parcel.season_on_parcel_uuid,
            parcel_uuid: parcel.uuid,
            surface: parcel.surface,
            netto_weight_kg: division,
            contract_uuid: dataSource.selectedContracts?.length > 0 ? dataSource.selectedContracts[0].uuid : null,
          });
        });
      });

      const resultTotal = this.roundToDecimals(
        productDivision.reduce((sum, item) => sum + (Number(item.netto_weight_kg) || 0), 0),
        this.quantityDecimalPlaces,
      );
      const targetTotal = this.roundToDecimals(Number(nettoWeightKg) || 0, this.quantityDecimalPlaces);

      if (resultTotal !== targetTotal && productDivision.length > 0) {
        const delta = this.roundToDecimals(targetTotal - resultTotal, this.quantityDecimalPlaces);
        productDivision[productDivision.length - 1].netto_weight_kg = this.roundToDecimals(
          (Number(productDivision[productDivision.length - 1].netto_weight_kg) || 0) + delta,
          this.quantityDecimalPlaces,
        );
      }

      return productDivision;
    },
    /**
     * Entry point for building the `weighing_products` array before saving.
     *
     * Decides which division strategy to apply based on the provided options:
     *
     * Strategy A – Warehouse split (multi-warehouse field report):
     *   When `options.splitRows` is a non-empty array (the operator confirmed
     *   the WarehouseSplitPopup), delegates entirely to
     *   `buildProductDivisionFromWarehouseSplit`.  Each split row maps to one
     *   warehouse; parcels within the same legal entity are sub-divided
     *   proportionally by surface area.
     *
     * Strategy B – Parcel-surface proportional split (single warehouse):
     *   When `sourceData.weighing_type_id === WEIGHING_TYPES.FIELD_REPORT` and
     *   at least one parcel is selected, the netto weight is distributed across
     *   all parcels proportionally to their surface.  The last parcel receives
     *   the residual to ensure the sum matches `netto_weight_kg` exactly.
     *   All entries share the same recipient warehouse
     *   (`sourceData.recipient_warehouse_uuid`).
     *
     * Strategy C – Single entry (non-field-report or no parcels):
     *   A single product-division entry is created with the full netto weight
     *   and no parcel association.
     *
     * Partial / unfinished weighing support:
     *   When the netto weight has not yet been determined (e.g. only one of the
     *   two scale measurements has been made), `netto_weight_kg` will be 0.
     *   In that case all three strategies still produce entries — with
     *   `netto_weight_kg: 0` — so that product, warehouse, and parcel
     *   associations are preserved in the payload even before weighing is
     *   complete.  The function returns an empty array ONLY when no meaningful
     *   data is available at all (no product, no recipient warehouse, no issuer
     *   warehouse, and no parcels selected).
     *
     * @param {number}  netto_weight_kg        - Total net weight in kg to distribute
     *                                           (may be 0 for unfinished weighings).
     * @param {Object}  [options={}]            - Optional configuration.
     * @param {Object}  [options.formData]      - Override for the form data source;
     *                                           defaults to `this.formData`.
     * @param {Object[]|null} [options.splitRows] - Confirmed warehouse split rows from
     *                                           the WarehouseSplitPopup, or null/empty
     *                                           when no multi-warehouse split was done.
     * @returns {Object[]} Array of weighing-product division entries for the API payload,
     *                     or an empty array when no data is available.
     */
    getProductDivision(netto_weight_kg, options = {}) {
      const sourceData = options.formData || this.formData;

      if(sourceData.weighing_type_id == WEIGHING_TYPES.CONTRACTED_MEASUREMENTS && !!sourceData.product_name){
        return [{
          product_uuid: null,
          product_name: sourceData.product_name,
          product_code: sourceData.product_code,
          label_uuid: sourceData.product_lot_category_label_uuid || null,
          recipient_warehouse_uuid: null,
          issuer_warehouse_uuid: null,
          partner_uuid: null,
          netto_weight_kg: roundToDecimals(netto_weight_kg, this.quantityDecimalPlaces),
          contract_uuid: null,
        }];
      }

      // Return empty only when there is truly nothing to build an entry from.
      // All three strategies below can produce valid partial entries with weight 0
      // for unfinished field-report weighings where measurements are not yet complete.
      const selectedRecipientWarehouseUuids = this.getSelectedRecipientWarehouseUuids(sourceData);
      const hasParcels = Array.isArray(sourceData.selectedParcels) && sourceData.selectedParcels.length > 0;
      const hasAnyData =
        !!sourceData.product_uuid ||
        hasParcels ||
        selectedRecipientWarehouseUuids.length > 0 ||
        !!sourceData.issuer_warehouse_uuid;

      if (!hasAnyData) {
        return [];
      }

      if (Array.isArray(options.splitRows) && options.splitRows.length > 0) {
        return this.buildProductDivisionFromWarehouseSplit(options.splitRows, netto_weight_kg, sourceData);
      }

      const productDivision = [];
      // Strategy B: field-report with parcels — distribute weight proportionally by parcel surface.
      if (
        sourceData.weighing_type_id == WEIGHING_TYPES.FIELD_REPORT &&
        hasParcels
      ) {
        // Delegate the proportional split to the shared helper.
        const divisions = divideWeightByParcels(
          sourceData.selectedParcels,
          netto_weight_kg,
          this.quantityDecimalPlaces,
        );

        sourceData.selectedParcels.forEach((parcel, idx) => {
          productDivision.push({
            product_uuid: sourceData.product_uuid,
            product_name: sourceData.product_name,
            product_code: sourceData.product_code,
            label_uuid: sourceData.product_lot_category_label_uuid || null,
            recipient_warehouse_uuid: sourceData.recipient_warehouse_uuid || selectedRecipientWarehouseUuids[0] || null,
            issuer_warehouse_uuid: sourceData.issuer_warehouse_uuid,
            partner_uuid: sourceData.partner_uuid,
            season_on_parcel_uuid: parcel.season_on_parcel_uuid,
            parcel_uuid: parcel.uuid,
            surface: parcel.surface,
            netto_weight_kg: divisions[idx],
            contract_uuid: sourceData.selectedContracts?.length > 0 ? sourceData.selectedContracts[0].uuid : null,
          });
        });
      } else {
        // Strategy C: non-field-report or no parcels — single entry with full weight.
        // netto_weight_kg may be 0 for an unfinished weighing; the entry is still
        // built so product / warehouse associations are kept in the payload.
        productDivision.push({
          product_uuid: sourceData.product_uuid,
          product_name: sourceData.product_name,
          product_code: sourceData.product_code,
          label_uuid: sourceData.product_lot_category_label_uuid || null,
          recipient_warehouse_uuid: sourceData.recipient_warehouse_uuid || selectedRecipientWarehouseUuids[0] || null,
          issuer_warehouse_uuid: sourceData.issuer_warehouse_uuid,
          partner_uuid: sourceData.partner_uuid,
          netto_weight_kg: roundToDecimals(netto_weight_kg, this.quantityDecimalPlaces),
          contract_uuid: sourceData.selectedContracts?.length > 0 ? sourceData.selectedContracts[0].uuid : null,
        });
      }
      return productDivision;
    },
    async loadProductLotCategoryLabels() {
      const response = await this.getLabels({
        page: 1,
        limit: 1000,
        type_code: "PRODUCT_LOT_CATEGORY",
      });

      this.productLotCategoryLabels = Array.isArray(response?.data?.data) ? response.data.data : [];
      return this.productLotCategoryLabels;
    },
    async resolveProductLabelClassificationBeforeSave(dataToSave) {
      if (!this.productLabelClassificationRequiredOnFieldReport) {
        return true;
      }

      const isFieldReport = parseInt(dataToSave?.weighing_type_id) === WEIGHING_TYPES.FIELD_REPORT;
      const withStockMovement = !!dataToSave?.create_inventory_movement;
      if (!isFieldReport || !withStockMovement) {
        return true;
      }

      const productUuid = dataToSave?.product_uuid;
      if (!productUuid) {
        return true;
      }

      const productData = await this.getCachedSmProduct(productUuid);
      if (!productData?.lot_management) {
        return true;
      }

      if (dataToSave.product_lot_category_label_uuid) {
        return true;
      }

      const labels = await this.loadProductLotCategoryLabels();
      if (!Array.isArray(labels) || labels.length === 0) {
        errorMessage(this, this.$t("scale.weighing_on_scale.error_product_label_classification_no_options"));
        return false;
      }

      const popupResult = await this.$refs.productLotCategoryLabelPopup?.open({
        labels: labels,
        selectedUuid: dataToSave.product_lot_category_label_uuid || null,
      });

      if (!popupResult) {
        return false;
      }

      if (popupResult?.action === "save_without_label") {
        dataToSave.product_lot_category_label_uuid = null;
        this.formData.product_lot_category_label_uuid = null;
        return true;
      }

      if (!popupResult?.label_uuid) {
        return false;
      }

      dataToSave.product_lot_category_label_uuid = popupResult.label_uuid;
      this.formData.product_lot_category_label_uuid = popupResult.label_uuid;
      return true;
    },
    roundToDecimals(value, decimals) {
      return roundToDecimals(value, decimals);
    },
    handleDownloadPdf(savedData) {
      this.downloadPdf({
        url: savedData.download_url,
        filename: savedData.download_filename,
        response_type: "blob",
      }).catch((e) => {
        console.error("Error downloading file:", e);
      });
    },
  },
  mounted() {
    this.getGroupByCode({
      code: "WEIGHING_SCALE",
    }).then((response) => {
      let _setting = response.items.find((obj) => obj.code == "WEIGHING_SCALE_ANALYZER_DATA_ENABLED");
      this.analyzerDataEnabled = false;
      if (_setting !== undefined) {
        this.analyzerDataEnabled = _setting?.value == "1";
      }

      let _setting_field_report_usa = response.items.find((obj) => obj.code == "WEIGHING_SCALE_FIELD_REPORT_USA_AUTOMATION_ENABLED");
      this.fieldReportUsaAutomationEnabled = false;
      if (_setting_field_report_usa !== undefined) {
        this.fieldReportUsaAutomationEnabled = _setting_field_report_usa?.value == "1";
      }

      let _setting_stock_def = response.items.find((obj) => obj.code == "WEIGHING_SCALE_CREATE_STOCK_MOVEMENT_DEF_TRUE");
      if (_setting_stock_def !== undefined) {
        this.formData.create_inventory_movement = _setting_stock_def?.value == "1";
      }

      let _setting_product_label_required = response.items.find(
        (obj) => obj.code == "WEIGHING_SCALE_PRODUCT_LABEL_CLASSIFICATION_REQUIRED",
      );
      this.productLabelClassificationRequiredOnFieldReport = false;
      if (_setting_product_label_required !== undefined) {
        this.productLabelClassificationRequiredOnFieldReport = _setting_product_label_required?.value == "1";
      }
    });

    if (this.$route.params.uuid && this.$route.params.uuid !== "add") {
      this.formData.uuid = this.$route.params.uuid;
      this.isLoading = true;
      setTimeout(() => {
        this.fetchWeighingOnScaleData(this.$route.params.uuid);
      }, 500);
    } else {
      this.formData.uuid = null;
      this.title = this.$t("scale.weighing_on_scale.add_scale");

      const transferSourceUuid = sessionStorage.getItem("weighing_transfer_source_uuid");
      if (transferSourceUuid) {
        sessionStorage.removeItem("weighing_transfer_source_uuid");
        this.isLoading = true;
        setTimeout(() => {
          this.fetchWeighingOnScaleData(transferSourceUuid, true);
        }, 500);
      }
    }

    this.getWarehouses({
      enablePagination: 0,
      limit: 1000,
      filter: [["is_active", "=", 1]],
    });
  },
};
</script>

<style scoped>
.view {
  height: calc(100% - 70px);
}

.weighing-on-scale-container {
  display: flex;
  height: 100%;
  width: 100%;
}

.weighing-on-scale-step-content {
  padding: 20px;
}

.weighing-on-scale-dx-tabpanel {
  width: 100%;
  height: 100%;
  min-height: 450px;
  /* Set your desired minimum height */
}

/* Left Box */
.left-box {
  flex: 1;
  /* Fill the remaining space */
  background-color: transparent;
  display: flex;
  flex-direction: column;
}
</style>

<style>
/* Hide navigation buttons in DxTabPanel */
.weighing-on-scale-step-content .dx-tabpanel-tabs {
  display: none !important;
}

.weighing-on-scale-step-content .dx-multiview-wrapper {
  border: none !important;
}

.weighing-on-scale-container .dx-selectbox {
  margin-top: 8px !important;
}

.weighing-on-scale-container .dx-textbox {
  margin-top: 8px !important;
}
</style>
